import React, { ElementType, FC, ImgHTMLAttributes } from 'react';
import type { ImageIntentContext } from '../../../../utils/imageIntentReporter';
import useImageIntentFriction, {
  FrictionProps,
} from './useImageIntentFriction';

// The friction props own these handlers and the data attribute, so a caller
// cannot pass its own (they would be silently replaced).
type PreviewImageAttributes = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  keyof FrictionProps
>;

export interface IntentPreviewImageProps extends PreviewImageAttributes {
  // What a save attempt on this image is reported with. Name the gallery
  // (all three fields, or NO_IMAGE_INTENT_GALLERY for none) whenever the
  // Redux gallery may not be this image's; otherwise the hook fills it in.
  context: ImageIntentContext;
  // Rendered in place of a plain <img>, so a caller keeps its styled image.
  component?: ElementType;
}

// A preview <img> with the save-intent friction of useImageIntentFriction:
// every right-click, drag and long-press is reported, and blocked as the
// tracker decides. For screens that cannot call the hook themselves (legacy
// class components) or that render a lone image. It registers no Cmd/Ctrl+S
// surface: render one ImageIntentSaveShortcut per screen for that.
const IntentPreviewImage: FC<IntentPreviewImageProps> = ({
  context,
  component: Image = 'img',
  ...imageProps
}) => {
  const { getFrictionProps } = useImageIntentFriction();

  return <Image {...imageProps} {...getFrictionProps(context)} />;
};

export default IntentPreviewImage;
